import React, { FunctionComponent, useState } from 'react';
import { FormState, ControllerRenderProps } from 'react-hook-form';
import { cn } from '@companion-professional/webutils';
import { Field } from './dynamicFormSchema';
import { FieldValues } from 'react-hook-form/dist/types/fields';
import { DynamicFieldComponent } from './dynamicFields/DynamicFieldComponentBase';
import { FormFieldError } from '../FormFieldError';
import { FormFunctions } from './DynamicFormDisplay';
import { customBehaviors } from './customBehaviors';
import { ExtraHelp } from '../ExtraHelp';

interface DynamicFieldSharedProps {
  // reactHookField is the react-hook-form field that is used to control the input field.
  reactHookField: ControllerRenderProps;

  // watch is a function that comes from react-hook-form and can be used to watch the values of the other form fields.
  watch: (fieldValues: FieldValues) => {};
  className?: string;
}

export interface DynamicFieldProps extends DynamicFieldSharedProps {
  field: Field;
  onChange: (...event: any[]) => void;
}

interface DynamicFieldWithDynamicComponentAndFormStateProps extends DynamicFieldSharedProps {
  dynamicFieldComponent: DynamicFieldComponent<Field, any>;
  formState: FormState<FieldValues>;
  formFunctions: FormFunctions;
}

// DynamicField is a component that takes a field and renders the appropriate input field.
export const DynamicField: FunctionComponent<DynamicFieldWithDynamicComponentAndFormStateProps> = ({
  dynamicFieldComponent,
  className,
  formState,
  reactHookField,
  formFunctions,
  ...props
}) => {
  const [highlight, setHighlight] = useState(false);
  const { field } = dynamicFieldComponent;

  return (
    <div
      className={cn('flex w-full flex-col justify-start leading-none', className, {
        'rounded-lg bg-accent': highlight
      })}
    >
      <div className="flex flex-row items-start p-4">
        <div className="w-full">
          <dynamicFieldComponent.Component
            field={field}
            reactHookField={reactHookField}
            {...props}
            onChange={(value) => {
              // If the field has custom behaviors, we run them here.
              if (field?.customBehaviors) {
                for (const [behaviorName, behaviorContents] of Object.entries(field.customBehaviors)) {
                  if (behaviorContents?.enabled && customBehaviors[behaviorName]) {
                    value = customBehaviors[behaviorName](value, formFunctions, behaviorContents?.options);
                  }
                }
              }
              return reactHookField.onChange(value);
            }}
          />
        </div>
        {field?.extraHelp ? <ExtraHelp markdown={field.extraHelp.markdown} onHoverChange={setHighlight} /> : null}
      </div>
      <FormFieldError fieldName={field.name} formState={formState} />
    </div>
  );
};
